// @flow

import _ from 'lodash';
import { update, $none, $set } from 'qim';
import { routeTo } from 'redux-router-kit';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';

import { Profile } from '@zapier/shared-entities';
import { notify } from 'app/developer-v3/store/shared/actions';
import {
  selectDefinitionOverride,
  selectServiceKey,
} from 'app/developer-v3/store/build/selectors';
import {
  updateOverrideWithService,
  updateService,
} from 'app/developer-v3/store/build/utils';

import {
  currentAppDefinitionEntityId,
  currentAppId,
  currentAppVersion,
} from 'app/developer-v3/selectors';
import {
  createKeyFromService,
  getServiceUrl,
  isActionType,
  titleize,
} from 'app/developer-v3/utils';
import {
  saveServiceUpdates,
  trackEvent,
  updateAppDefinition,
} from 'app/developer-v3/Actions';

import {
  NEW_SERVICE_FIELD,
  SERVICE_TYPE,
  TRACKING_EVENTS,
} from 'app/developer-v3/constants';

import type { Dispatch, GetState } from 'app/common/types';
import type { AppService } from 'app/developer-v3/types';
import type { ServiceType } from 'app/developer-v3/types/service';
import type { LegacySchema } from 'app/developer-v3/platformSchema/legacy';
import type { Middleware } from 'app/developer-v3/platformSchema/middleware';

export {
  deleteAction,
  deleteSearchOrCreate,
  saveSearchOrCreate,
} from 'app/developer-v3/Actions';

const trackNewService = (serviceType: ServiceType, serviceKey: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const eventType =
      serviceType === SERVICE_TYPE.triggers
        ? TRACKING_EVENTS.NEW_TRIGGER_STARTED
        : TRACKING_EVENTS.NEW_ACTION_STARTED;

    return dispatch(
      trackEvent(eventType, {
        app_id: currentAppId(state),
        app_version: currentAppVersion(state),
        app_version_id: currentAppDefinitionEntityId(state),
        customuser_id: Profile.selectors.all.currentProfileId(state),
        is_staff: Profile.selectors.isStaff(
          Profile.selectors.all.currentProfile(state)
        ),
        serviceKey,
        serviceType,
      })
    );
  };
};

export const createNewService = (
  serviceType: ServiceType,
  newService: AppService
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { key: newServiceKey } = newService;
    const state = getState();
    const appDefinitionOverride = selectDefinitionOverride(state);

    if (_.get(appDefinitionOverride, [serviceType, newServiceKey])) {
      dispatch(
        notify.failure(`Key: ${newServiceKey} already exists in ${serviceType}`)
      );
      return;
    }

    const isSuccess = await dispatch(
      updateAppDefinition(
        updateOverrideWithService({
          serviceType,
          service: updateService(serviceType, newService, state),
          state,
        })
      )
    );

    if (!isSuccess) {
      dispatch(
        notify.failure(
          `Bargle! Sorry, there was a problem creating a new ${titleize(
            serviceType
          )}`
        )
      );
      return;
    }

    dispatch(trackNewService(serviceType, newServiceKey));

    const serviceName = isActionType(serviceType)
      ? `${titleize(serviceType)} Action`
      : titleize(serviceType);
    dispatch(notify.success(`New ${serviceName} created successfully!`));

    const options = {
      ...RouterSelectors.getParams(state),
      [createKeyFromService(serviceType)]: newServiceKey,
      ...(isActionType(serviceType) && { actionType: serviceType }),
    };
    dispatch(routeTo(`${getServiceUrl(serviceType, options)}/input`));
  };
};

export const saveServiceSettings = (
  serviceType: ServiceType,
  serviceUpdate: AppService
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (NEW_SERVICE_FIELD === selectServiceKey(state)) {
      return dispatch(createNewService(serviceType, serviceUpdate));
    }

    const updatedService = updateService(serviceType, serviceUpdate, state);
    const isSuccess = await dispatch(
      saveServiceUpdates(serviceType, updatedService)
    );

    if (isSuccess) {
      dispatch(
        notify.success(
          `${titleize(serviceType)}: ${serviceUpdate.key} has been updated!`
        )
      );
    }

    return undefined;
  };
};

export const saveLegacy = (legacy: LegacySchema) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const override = selectDefinitionOverride(state);
  const definition = update(['legacy', $set(legacy)], override);

  const isSuccess = await dispatch(updateAppDefinition(definition));

  if (isSuccess) {
    dispatch(notify.success('Updated legacy scripting.'));
  }
};

export const saveMiddleware = (middleware: Middleware) => async (
  dispatch: Dispatch,
  getState: GetState
) => {
  const state = getState();
  const override = selectDefinitionOverride(state);

  const trimmedBeforeRequest = middleware.beforeRequest.trim();
  const trimmedAfterResponse = middleware.afterResponse.trim();

  const definition = update(
    [
      [
        'beforeRequest',
        trimmedBeforeRequest
          ? $set([
              {
                args: ['request', 'z', 'bundle'],
                source: trimmedBeforeRequest,
              },
            ])
          : $none,
          ],
      [
      'afterResponse',
      trimmedAfterResponse
        ? $set([
            {
              args: ['response', 'z', 'bundle'],
              source: trimmedAfterResponse,
            },
          ])
        : $none,
        ],
    ],
    override
  );

  const isSuccess = await dispatch(updateAppDefinition(definition));

  if (isSuccess) {
    dispatch(notify.success('Updated middleware scripting.'));
  }
};
